import React from "react";
import { Autocomplete, Grid, TextField } from "@mui/material";
import {
  getAllClientBoxes,
  getAllClientBrands,
  getAllClientProducerFarms,
  getAllClientProducers,
  getClientIdByToken,
  validTaskCodeByToken,
} from "../../../../libs/apis";
import {
  Button,
  Divider,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
} from "antd";
import { useWorkflowStore } from "../../store";
import {
  ClientI,
  ClientProducerI,
  ClientProducerFarmI,
  ClientBoxI,
  ClientBrandI,
} from "../../../../libs/interfaces";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DateFormatHelper } from "../../../../libs/helpers";
import { Environments } from "../../../../libs/enums";
import { DividerComponent } from "../../../../libs/shared";
import { useRouterStore } from "../../../../libs/stores";
import { FileSyncOutlined } from "@ant-design/icons";

dayjs.extend(customParseFormat);
const { TextArea } = Input;

interface FirstComponentProps extends PropsGenericI {
  clients: ClientI[];
  enqueueSnackbar: (message: string, options?: any) => void;
}

interface PropsGenericI {
  width: string;
}

export const FirstComponent = ({
  clients,
  width,
  enqueueSnackbar,
}: FirstComponentProps) => {
  const [showExporters, setShowExporters] = React.useState(false);

  React.useEffect(() => {
    handleGetData();
  }, []);

  const handleGetData = async () => {
    const { data: dataTask } = await validTaskCodeByToken(
      Environments.TASK_CONTAINER_EMPTY_ASSIGN
    );

    if (!dataTask) return;
    if (dataTask.status) setShowExporters(true);
  };

  return (
    <Grid
      container
      spacing={2}
      justifyContent="flex-start"
      alignContent="flex-start"
      alignItems="flex-start"
      sx={{
        minHeight: `calc(100vh - 105px)`,
        maxHeight: `calc(100vh - 105px)`,
        height: `calc(100vh - 105px)`,
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        pt: 7,
        width: "100%",
      }}
    >
      <NumeroContenedor />

      {showExporters ? null : (
        <>{width === "xs" ? null : <Grid item xs={3}></Grid>}</>
      )}
      <Exporters
        clients={clients}
        showExporters={showExporters}
        enqueueSnackbar={enqueueSnackbar}
      />

      <Magap />

      <DividerComponent label="TRANSPORTE" />
      <Conductor width={width} />
      <CompañíaDeTransporte />

      <DividerComponent label="CALIDAD - GANCHO" />
      <Calidad width={width} />

      <Gancho width={width} />

      <DividerComponent label="HORAS DE PROCESO" />

      <InicioProceso />

      <LlegadaContenedor />

      <AperturaContenedor />

      <InicioDeCarga />

      <DividerComponent label="OBSERVACIÓN" />
      <Observacion />

      <Grid item xs={12} sx={{ height: 20 }}></Grid>
    </Grid>
  );
};

const NumeroContenedor = () => {
  const container = useWorkflowStore((state) => state.container);
  const setContainer = useWorkflowStore((state) => state.setContainer);

  return (
    <Grid item xs={12} sm={6} md={6}>
      <Input
        addonBefore={<p style={{ fontWeight: "bold" }}>N° Contenedor</p>}
        value={container}
        onChange={(e) => {
          setContainer(e.target.value.toUpperCase());
        }}
        onBlur={(e) => {
          setContainer(e.target.value.trim());
        }}
      />
    </Grid>
  );
};

interface ClientsProps {
  clients: ClientI[];
  showExporters: boolean;
  enqueueSnackbar: (message: string, options?: any) => void;
}

const Exporters = ({
  clients,
  showExporters,
  enqueueSnackbar,
}: ClientsProps) => {
  const elClientId = useRouterStore((state) => state.clientId);

  const wfClientId = useWorkflowStore((state) => state.clientId);
  const producerId = useWorkflowStore((state) => state.producerId);

  const setWorkflowClientId = useWorkflowStore((state) => state.setClientId);
  const setWorkflowClient = useWorkflowStore((state) => state.setClient);
  const resetWorkflowClient = useWorkflowStore((state) => state.resetClient);

  const clientName = useWorkflowStore((state) => state.client);

  const [producers, setProducers] = React.useState<ClientProducerI[]>([]);
  const [farms, setFarms] = React.useState<ClientProducerFarmI[]>([]);
  const [boxes, setBoxes] = React.useState<ClientBoxI[]>([]);
  const [brands, setBrands] = React.useState<ClientBrandI[]>([]);
  const resetProducer = useWorkflowStore((state) => state.resetProducer);
  const setProducer = useWorkflowStore((state) => state.setProducer);
  const setProducerId = useWorkflowStore((state) => state.setProducerId);

  React.useEffect(() => {
    (async () => {
      try {
        // Si es un usuario normal (NO admin) y tiene elClientId
        if (elClientId) {
          // Guardamos ese clientId en el workflow store, para que
          // todo el flujo use el MISMO ID de cliente.
          setWorkflowClientId(elClientId);
          await loadClientData(elClientId);
        } else {
          // Si es un usuario admin (no tiene elClientId)...
          // 1) Revisa si ya tenemos un clientId en workflow store
          if (wfClientId) {
            // Si lo tenemos, cargamos su data
            await loadClientData(wfClientId);
          } else {
            // 2) Si no tenemos, tratamos de obtenerlo por token
            const { data: dataClient } = await getClientIdByToken();
            if (dataClient) {
              setWorkflowClientId(dataClient);
              await loadClientData(dataClient);
            }
          }
        }

        if (producerId) {
          await loadFarms(producerId);
        }
      } catch (err: any) {
        enqueueSnackbar(err.toString(), { variant: "error" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------
  // 2) Función para cargar datos principales de un "cliente"
  // ----------------------------------------------------------------
  const loadClientData = async (clientNumber: number) => {
    try {
      const producersData = await fetchProducers(clientNumber);
      setProducers(producersData);

      const { data: dataBox } = await getAllClientBoxes(clientNumber);
      if (dataBox) setBoxes(dataBox);

      const { data: dataBrands } = await getAllClientBrands(clientNumber);
      if (dataBrands) setBrands(dataBrands);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const loadClientDataManual = async (clientNumber: number) => {
    try {
      loadClientData(clientNumber);
      setProducer(null);
      setProducerId(null);
      setFarms([]);
      resetProducer();
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const fetchProducers = async (clientNumber: number) => {
    try {
      const { data: dataProducers } = await getAllClientProducers(clientNumber);
      return dataProducers ?? [];
    } catch (err: any) {
      console.error(err);
      return [];
    }
  };

  const loadFarms = async (producerId: number) => {
    try {
      const { data: dataFarms } = await getAllClientProducerFarms(producerId);
      if (dataFarms) setFarms(dataFarms);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectClient = async (_: any, newValue: string | null) => {
    try {
      setWorkflowClient(newValue ?? "");
      resetWorkflowClient();

      if (!newValue) {
        setWorkflowClientId(null);
        setProducers([]);
        setFarms([]);
        setBoxes([]);
        setBrands([]);
        return;
      }

      const found = clients.find((el) => el.name === newValue);
      if (!found) return;

      setWorkflowClientId(found.id!);
      await loadClientData(found.id!);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const isStandardUser = Boolean(elClientId); // o más claro: elClientId !== null
  const disabledAutocomplete = isStandardUser; // Si es un usuario normal, no debe cambiar Exportador

  return (
    <>
      {showExporters && (
        <Grid item xs={12} sm={6} md={6}>
          <Autocomplete
            id="clients_list"
            value={clientName || ""}
            options={clients.map((el: ClientI) => el.name) ?? []}
            onChange={handleSelectClient}
            fullWidth
            size="small"
            renderInput={(params) => (
              <TextField {...params} label="Exportador" />
            )}
          />
        </Grid>
      )}

      <DividerComponent label="EXPORTADORA">
        <Popconfirm
          title="¿Estás seguro de refrescar los productores?"
          onConfirm={() => {
            loadClientDataManual(elClientId! ?? wfClientId!);
          }}
          okText="Proceder"
          cancelText="Cancelar"
        >
          <Button icon={<FileSyncOutlined />} size="middle" type="dashed">
            Productores
          </Button>
        </Popconfirm>
      </DividerComponent>

      {/* Producers / Farms / Brands / Boxes */}
      <Producers
        producers={producers}
        onLoadFarms={loadFarms}
        onClearFarms={() => setFarms([])}
        enqueueSnackbar={enqueueSnackbar}
      />
      <Farms farms={farms} />
      <Brands brands={brands} />
      <Boxes boxes={boxes} />
    </>
  );
};

interface ProducersPropsI {
  producers: ClientProducerI[];
  onLoadFarms: (producerId: number) => Promise<void>;
  onClearFarms: () => void;
  enqueueSnackbar: (message: string, options?: any) => void;
}

export const Producers = ({
  producers,
  onLoadFarms,
  onClearFarms,
  enqueueSnackbar,
}: ProducersPropsI) => {
  const producer = useWorkflowStore((state) => state.producer);
  const setProducer = useWorkflowStore((state) => state.setProducer);
  const setProducerId = useWorkflowStore((state) => state.setProducerId);
  const resetProducer = useWorkflowStore((state) => state.resetProducer);

  const handleSelectProducer = async (newValue: string | null) => {
    try {
      setProducer(newValue ?? null);
      resetProducer();

      if (!newValue) {
        // Si se limpia el select, limpiamos farms en el padre
        onClearFarms();
        return;
      }

      const found = producers.find((el) => el.name === newValue);
      if (!found) return;

      setProducerId(found.id!);
      await onLoadFarms(found.id!);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Select
        options={producers.map((el) => ({ label: el.name, value: el.name }))}
        value={producer}
        placeholder="Productor"
        // suffixIcon={
        //   producer ? (
        //     <p style={{ fontWeight: "bold", fontSize: 6, color: "black" }}>
        //       Productor
        //     </p>
        //   ) : (
        //     <p style={{ fontWeight: "bold", color: "black" }}>Productor</p>
        //   )
        // }
        allowClear
        onChange={handleSelectProducer}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

interface FarmsPropsI {
  farms: ClientProducerFarmI[];
}

const Farms = ({ farms }: FarmsPropsI) => {
  const balerCode = useWorkflowStore((state) => state.balerCode);
  const farm = useWorkflowStore((state) => state.farm);
  const zone = useWorkflowStore((state) => state.zone);
  const setFarm = useWorkflowStore((state) => state.setFarm);
  const setFarmId = useWorkflowStore((state) => state.setFarmId);
  const setBalerCode = useWorkflowStore((state) => state.setBalerCode);
  const setZone = useWorkflowStore((state) => state.setZone);

  const resetFarm = useWorkflowStore((state) => state.resetFarm);

  const handleSelectClientProducerFarm = (newValue: string | null) => {
    setFarm(newValue ?? null);

    if (!newValue) {
      resetFarm();
      return;
    }

    const fill = farms.find((el) => el.name === newValue);
    if (!fill) return;

    setFarmId(fill.id!);
    setZone(fill.zone);
    setBalerCode(fill.balerCode);
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Select
          options={farms.map((el) => ({ label: el.name, value: el.name }))}
          value={farm}
          placeholder="Hacienda"
          // suffixIcon={
          //   farm ? (
          //     <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
          //       Hacienda
          //     </p>
          //   ) : (
          //     <p style={{ fontWeight: "bold", color: "black" }}>Hacienda</p>
          //   )
          // }
          allowClear={true}
          onChange={handleSelectClientProducerFarm}
          style={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          value={zone}
          readOnly
          suffix={
            zone ? (
              <p style={{ fontWeight: "bold", color: "black", fontSize: 8 }}>
                Zona
              </p>
            ) : (
              <p style={{ fontWeight: "bold", color: "black", fontSize: 11 }}>
                Zona
              </p>
            )
          }
          style={{ width: "100%" }}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          value={balerCode}
          onChange={(e) => setBalerCode(e.target.value)}
          suffix={
            balerCode ? (
              <p style={{ fontWeight: "bold", color: "black", fontSize: 8 }}>
                Empacadora
              </p>
            ) : (
              <p style={{ fontWeight: "bold", color: "black", fontSize: 11 }}>
                Código de Empacadora
              </p>
            )
          }
          style={{ width: "100%" }}
        />
      </Grid>
    </>
  );
};

const Brands = ({ brands }: { brands: ClientBrandI[] }) => {
  const brand = useWorkflowStore((state) => state.brand);
  const setBrand = useWorkflowStore((state) => state.setBrand);
  const setBrandId = useWorkflowStore((state) => state.setBrandId);

  const handleSelectBrand = (newValue: string | null) => {
    if (!newValue) {
      setBrand(null);
      setBrandId(null);
      return;
    }

    setBrand(newValue);

    const fill = brands.find((el) => el.name === newValue);
    if (!fill) return;

    setBrandId(fill.id!);
  };

  return (
    <Grid item xs={6} md={6} lg={4}>
      <Select
        options={brands.map((el) => ({ label: el.name, value: el.name }))}
        value={brand}
        placeholder="Marca"
        // suffixIcon={
        //   brand ? (
        //     <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
        //       Marca
        //     </p>
        //   ) : (
        //     <p style={{ fontWeight: "bold", color: "black" }}>Marca</p>
        //   )
        // }
        allowClear={true}
        onChange={handleSelectBrand}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const Boxes = ({ boxes }: { boxes: ClientBoxI[] }) => {
  const box = useWorkflowStore((state) => state.box);
  const setBox = useWorkflowStore((state) => state.setBox);
  const setBoxId = useWorkflowStore((state) => state.setBoxId);

  const handleSelectBox = (newValue: string | null) => {
    if (!newValue) {
      setBox(null);
      setBoxId(null);
      return;
    }

    setBox(newValue);

    const fill = boxes.find((el) => el.name === newValue);
    if (!fill) return;

    setBoxId(fill.id!);
  };

  return (
    <Grid item xs={6} md={6} lg={4}>
      <Select
        options={boxes.map((el) => ({ label: el.name, value: el.name }))}
        value={box}
        placeholder="Tipo de Caja"
        // suffixIcon={
        //   box ? (
        //     <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
        //       Caja
        //     </p>
        //   ) : (
        //     <p style={{ fontWeight: "bold", color: "black" }}>Tipo de Caja</p>
        //   )
        // }
        allowClear={true}
        onChange={handleSelectBox}
        style={{ width: "100%" }}
      />
    </Grid>
  );
};

const Magap = () => {
  const magap = useWorkflowStore((state) => state.magap);
  const setMagap = useWorkflowStore((state) => state.setMagap);

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Input
        style={{ width: "100%" }}
        value={magap || ""}
        onChange={(e) => setMagap(e?.target?.value?.toUpperCase() ?? "")}
        prefix={
          magap ? (
            <p style={{ fontWeight: "bold", fontSize: 8, color: "black" }}>
              Magap
            </p>
          ) : (
            <p style={{ fontWeight: "bold", color: "black" }}>Magap</p>
          )
        }
      />
    </Grid>
  );
};

const Conductor = ({ width }: { width: string }) => {
  const driver = useWorkflowStore((state) => state.driver);
  const setDriver = useWorkflowStore((state) => state.setDriver);

  const placa = useWorkflowStore((state) => state.placa);
  const setPlaca = useWorkflowStore((state) => state.setPlaca);

  return (
    <>
      {width === "xs" ? (
        <>
          <Grid item xs={12}>
            <Input
              value={driver || ""}
              onChange={(e) => setDriver(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Conductor
                </p>
              }
              placeholder="Nombre y Apellido"
              style={{ width: "100%" }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={placa || ""}
              onChange={(e) => setPlaca(e.target.value.toUpperCase())}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Placa
                </p>
              }
              placeholder=""
              style={{ width: "100%" }}
            />
          </Grid>
        </>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={driver || ""}
              onChange={(e) => setDriver(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Conductor
                </p>
              }
              placeholder="Nombre y Apellido"
              style={{ width: "60%" }}
            />
            <Input
              value={placa || ""}
              onChange={(e) => setPlaca(e.target.value.toUpperCase())}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Placa
                </p>
              }
              placeholder=""
              style={{ width: "40%" }}
            />
          </Space.Compact>
        </Grid>
      )}
    </>
  );
};

const CompañíaDeTransporte = () => {
  const companyTransport = useWorkflowStore((state) => state.companyTransport);
  const setCompanyTransport = useWorkflowStore(
    (state) => state.setCompanyTransport
  );

  return (
    <Grid item xs={12}>
      <Input
        value={companyTransport || ""}
        onChange={(e) => setCompanyTransport(e.target.value)}
        addonBefore={
          <p
            style={{
              fontSize: "14px",
              color: "black",
              width: "70px",
              fontWeight: "bold",
            }}
          >
            Compañía
          </p>
        }
        placeholder=""
      />
    </Grid>
  );
};

const Calidad: React.FC<PropsGenericI> = ({ width }) => {
  const calidad = useWorkflowStore((state) => state.calidad);
  const setCalidad = useWorkflowStore((state) => state.setCalidad);
  const calidadCi = useWorkflowStore((state) => state.calidadCi);
  const setCalidadCi = useWorkflowStore((state) => state.setCalidadCi);

  return (
    <>
      {width === "xs" ? (
        <>
          <Grid item xs={12}>
            <Input
              value={calidad || ""}
              onChange={(e) => setCalidad(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Calidad
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={calidadCi || ""}
              onChange={(e) => setCalidadCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Grid>
          <Divider dashed />
        </>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={calidad || ""}
              onChange={(e) => setCalidad(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Calidad
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
            <Input
              value={calidadCi || ""}
              onChange={(e) => setCalidadCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Space.Compact>
        </Grid>
      )}
    </>
  );
};

const Gancho: React.FC<PropsGenericI> = ({ width }) => {
  const gancho = useWorkflowStore((state) => state.gancho);
  const setGancho = useWorkflowStore((state) => state.setGancho);
  const ganchoCi = useWorkflowStore((state) => state.ganchoCi);
  const setGanchoCi = useWorkflowStore((state) => state.setGanchoCi);

  return (
    <>
      {width === "xs" ? (
        <>
          <Grid item xs={12} sx={{ mt: -2 }}>
            <Input
              value={gancho || ""}
              onChange={(e) => setGancho(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Gancho
                </p>
              }
              placeholder="Nombre Y Apellido"
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              value={ganchoCi || ""}
              onChange={(e) => setGanchoCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "black",
                    width: "70px",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Grid>
        </>
      ) : (
        <Grid item xs={12}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={gancho || ""}
              onChange={(e) => setGancho(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Gancho
                </p>
              }
              placeholder="Nombre Y Apellido"
            />

            <Input
              value={ganchoCi || ""}
              onChange={(e) => setGanchoCi(e.target.value)}
              addonBefore={
                <p
                  style={{
                    fontSize: width === "xs" ? "11px" : "14px",
                    fontWeight: "bold",
                  }}
                >
                  Cédula
                </p>
              }
            />
          </Space.Compact>
        </Grid>
      )}
    </>
  );
};

const InicioProceso = () => {
  const setStartProcess = useWorkflowStore((state) => state.setStartProcess);
  const startProcess = useWorkflowStore((state) => state.startProcess);

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
    return timeRegex.test(time);
  };

  // Manejar el cambio en el campo de texto (sin validación)
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setStartProcess(value); // Actualizar el valor del input en tiempo real
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlur = () => {
    if (startProcess === "") {
      setStartProcess(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(startProcess)) {
      setStartProcess(startProcess); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setStartProcess(startProcess || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Space.Compact style={{ width: "100%" }}>
        <Button
          onClick={() => {
            const dateFormat = new DateFormatHelper();
            setStartProcess(dateFormat.getTime());
          }}
          style={{ fontWeight: "bold", width: "150px" }}
        >
          Inicio de Proceso
        </Button>

        <Input
          style={{ width: "calc(100% - 150px)" }}
          placeholder="HH:mm (ej. 14:30)"
          value={startProcess || ""}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          maxLength={5} // Limitar a 5 caracteres (HH:mm)
        />

        {/* <TimePicker
          style={{ width: "calc(100% - 150px)" }}
          format={hourFormat}
          needConfirm={false}
          inputReadOnly={true}
          value={startProcess ? dayjs(startProcess, hourFormat) : null}
          onChange={(tim, time) => {
            setStartProcess(time.toString());
          }}
        /> */}
      </Space.Compact>
    </Grid>
  );
};

const LlegadaContenedor = () => {
  const setContainerArrival = useWorkflowStore(
    (state) => state.setContainerArrival
  );

  const containerArrival = useWorkflowStore((state) => state.containerArrival);

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
    return timeRegex.test(time);
  };

  // Manejar el cambio en el campo de texto (sin validación)
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setContainerArrival(value); // Actualizar el valor del input en tiempo real
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlur = () => {
    if (containerArrival === "") {
      setContainerArrival(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(containerArrival)) {
      setContainerArrival(containerArrival); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setContainerArrival(containerArrival || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Space.Compact style={{ width: "100%" }}>
        <Button
          onClick={() => {
            const dateFormat = new DateFormatHelper();
            setContainerArrival(dateFormat.getTime());
          }}
          style={{ fontWeight: "bold", width: "150px" }}
        >
          Llegada Contenedor
        </Button>
        <Input
          style={{ width: "calc(100% - 150px)" }}
          placeholder="HH:mm (ej. 14:30)"
          value={containerArrival || ""}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          maxLength={5} // Limitar a 5 caracteres (HH:mm)
        />

        {/* <TimePicker
          style={{ width: "calc(100% - 150px)" }}
          format={hourFormat}
          needConfirm={false}
          inputReadOnly={true}
          value={containerArrival ? dayjs(containerArrival, hourFormat) : null}
          onChange={(tim, time) => {
            setContainerArrival(time.toString());
          }}
        /> */}
      </Space.Compact>
    </Grid>
  );
};

const AperturaContenedor = () => {
  const setContainerOpen = useWorkflowStore((state) => state.setContainerOpen);

  const containerOpen = useWorkflowStore((state) => state.containerOpen);

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
    return timeRegex.test(time);
  };

  // Manejar el cambio en el campo de texto (sin validación)
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setContainerOpen(value); // Actualizar el valor del input en tiempo real
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlur = () => {
    if (containerOpen === "") {
      setContainerOpen(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(containerOpen)) {
      setContainerOpen(containerOpen); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setContainerOpen(containerOpen || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Space.Compact style={{ width: "100%" }}>
        <Button
          onClick={() => {
            const dateFormat = new DateFormatHelper();
            setContainerOpen(dateFormat.getTime());
          }}
          style={{ fontWeight: "bold", width: "150px" }}
        >
          Apertura Contenedor
        </Button>
        <Input
          style={{ width: "calc(100% - 150px)" }}
          placeholder="HH:mm (ej. 14:30)"
          value={containerOpen || ""}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          maxLength={5} // Limitar a 5 caracteres (HH:mm)
        />
        {/* <TimePicker
          style={{ width: "calc(100% - 150px)" }}
          format={hourFormat}
          needConfirm={false}
          inputReadOnly={true}
          value={containerOpen ? dayjs(containerOpen, hourFormat) : null}
          onChange={(tim, time) => {
            setContainerOpen(time.toString());
          }}
        /> */}
      </Space.Compact>
    </Grid>
  );
};

const InicioDeCarga = () => {
  const startLoading = useWorkflowStore((state) => state.startLoading);
  const setStartLoading = useWorkflowStore((state) => state.setStartLoading);

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/; // HH:mm, horas 00-23, minutos 00-59
    return timeRegex.test(time);
  };

  // Manejar el cambio en el campo de texto (sin validación)
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    // Eliminar cualquier carácter que no sea número
    value = value.replace(/[^0-9]/g, "");

    // Si hay 3 caracteres y no hay dos puntos, añadirlos
    if (value.length >= 3 && !value.includes(":")) {
      value = `${value.slice(0, 2)}:${value.slice(2)}`;
    }

    // Limitar a 5 caracteres (HH:mm)
    if (value.length > 5) {
      value = value.slice(0, 5);
    }

    setStartLoading(value); // Actualizar el valor del input en tiempo real
  };

  // Manejar la validación cuando el campo pierde el foco
  const handleBlur = () => {
    if (startLoading === "") {
      setStartLoading(""); // Permitir borrar el campo
      return;
    }

    if (validateTimeFormat(startLoading)) {
      setStartLoading(startLoading); // Guardar el valor si es válido
    } else {
      message.error(
        "Formato de hora inválido. Usa HH:mm (por ejemplo, 14:30)."
      );
      setStartLoading(startLoading || ""); // Revertir al valor anterior si es inválido
    }
  };

  return (
    <Grid item xs={12} sm={6}>
      <Space.Compact style={{ width: "100%" }}>
        <Button
          onClick={() => {
            const dateFormat = new DateFormatHelper();
            setStartLoading(dateFormat.getTime());
          }}
          style={{ fontWeight: "bold", width: "150px" }}
        >
          Inicio de Carga
        </Button>
        <Input
          style={{ width: "calc(100% - 150px)" }}
          placeholder="HH:mm (ej. 14:30)"
          value={startLoading || ""}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          maxLength={5} // Limitar a 5 caracteres (HH:mm)
        />
        {/* <TimePicker
          style={{ width: "calc(100% - 150px)" }}
          format={hourFormat}
          needConfirm={false}
          inputReadOnly={true}
          value={startLoading ? dayjs(startLoading, hourFormat) : null}
          onChange={(tim, time) => {
            setStartLoading(time.toString());
          }}
        /> */}
      </Space.Compact>
    </Grid>
  );
};

const Observacion = () => {
  const observation = useWorkflowStore((state) => state.observation);

  const setObservation = useWorkflowStore((state) => state.setObservation);

  return (
    <Grid item xs={12}>
      <TextArea
        rows={4}
        placeholder="Observación"
        maxLength={500}
        showCount
        value={observation || ""}
        onChange={(e) => setObservation(e.target.value)}
      />
    </Grid>
  );
};
